import { useEffect, useState } from 'react';
import { FileCheck2, PlusCircle, RotateCcw, Trash2 } from 'lucide-react';

import ReusableModalWrapper from '../Modals/Common/ReusableModalWrapper';
import ActionButton from '../Buttons/ActionButton';
import Checkbox from '../Forms/Input/Checkbox';
import InputField from '../Forms/Input/InputField';
import PurchaseReturnDocumentSelector from './PurchaseReturnDocumentSelector';
import FiscalEventStatusBadge from './FiscalEventStatusBadge';
import FiscalEventDownloads from './FiscalEventDownloads';

import usePurchaseReturnEvent from '../../hooks/fiscal/usePurchaseReturnEvent';
import FiscalEventService from '../../services/fiscal/FiscalEventService';
import { FiscalEvent, FiscalEventStatus } from '../../types/fiscal/FiscalEvent';

interface Props {
  isOpen: boolean;
  initialPurchaseId?: number | null;
  onClose: () => void;
  onCreated?: () => void;
  /** Renderiza el contenido sin el modal flotante (usado como cuerpo de página). */
  embedded?: boolean;
}

const typeLabel = (type?: string) =>
  ({ '14': 'Factura de sujeto excluido' } as Record<string, string>)[type || ''] || type || '—';

const dateLabel = (date?: string) => {
  if (!date) return '—';
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? date : new Intl.DateTimeFormat('es-SV', { dateStyle: 'medium' }).format(parsed);
};

const money = (value?: number) => new Intl.NumberFormat('es-SV', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

const resultTitleByStatus: Partial<Record<FiscalEventStatus, string>> = {
  DRAFT: 'Borrador de Evento de Retorno guardado',
  VALIDATED: 'Evento de Retorno validado contra el schema oficial',
  SIGNED: 'Evento de Retorno firmado, pendiente de transmisión',
  PROCESSED: 'Evento de Retorno procesado por Hacienda',
  REJECTED: 'Hacienda rechazó el Evento de Retorno',
};

const resultMessageByStatus: Partial<Record<FiscalEventStatus, string>> = {
  DRAFT: 'El Evento de Retorno quedó guardado en estado DRAFT. El siguiente paso es validarlo contra el schema oficial fe-eret-v1.',
  VALIDATED: 'El JSON es válido contra el schema oficial. El siguiente paso es firmarlo.',
  SIGNED: 'El Evento de Retorno fue firmado correctamente. El siguiente paso es transmitirlo a Hacienda.',
  PROCESSED: 'El Evento de Retorno fue transmitido y procesado correctamente por Hacienda.',
  REJECTED: 'Hacienda recibió el Evento de Retorno, pero rechazó su contenido. Revise el detalle fiscal.',
};

const PurchaseReturnEventWizard = ({ isOpen, initialPurchaseId, onClose, onCreated, embedded = false }: Props) => {
  const {
    documents,
    selectedLines,
    quantities,
    unitCosts,
    discounts,

    preview,
    previewLoading,
    result,

    busy,

    request,
    validationErrors,

    purchaseReturnLineKey,

    handlerAddDocument,
    handlerRemoveDocument,

    handlerChangeSelection,
    handlerChangeQuantity,
    handlerChangeUnitCost,
    handlerChangeDiscount,
    handlerSetAll,

    handlerSubmit,
    handlerValidate,
    handlerSign,
    handlerSend,
    handlerResetPurchaseReturnEvent,
  } = usePurchaseReturnEvent({ isOpen, initialPurchaseId, onCreated });

  const [eventDetail, setEventDetail] = useState<FiscalEvent | null>(null);
  const [showAddDocument, setShowAddDocument] = useState(false);

  useEffect(() => {
    if (!result || (result.status !== 'REJECTED' && result.status !== 'PROCESSED')) {
      setEventDetail(null);
      return;
    }

    let cancelled = false;

    FiscalEventService.getById(result.id)
      .then((event) => {
        if (!cancelled) setEventDetail(event);
      })
      .catch(() => {
        if (!cancelled) setEventDetail(null);
      });

    return () => {
      cancelled = true;
    };
  }, [result?.id, result?.status]);

  const handleClose = () => {
    if (busy) {
      const confirmed = window.confirm(
        'Hay una petición activa. ¿Desea cerrar y dejar de esperar la respuesta?'
      );
      if (!confirmed) return;
    }

    handlerResetPurchaseReturnEvent();
    onClose();
  };

  const getLineError = (purchaseId: number, productId: number, availableQuantity: number) => {
    const key = purchaseReturnLineKey(purchaseId, productId);
    if (!selectedLines[key]) return undefined;
    const requested = Number(quantities[key] || 0);
    if (!Number.isFinite(requested) || requested <= 0) return 'La cantidad debe ser mayor que cero.';
    if (requested > availableQuantity) return 'La cantidad supera el saldo retornable.';
    return undefined;
  };

  const canSubmit =
    !!request.lines.length && !validationErrors.length && !previewLoading && !!preview?.valid;

  const body = (
    <div className="space-y-5">

      {!result && (
        <>
          {(!initialPurchaseId || showAddDocument) && (
            <PurchaseReturnDocumentSelector
              selected={documents.flatMap((item) => (item.candidate ? [item.candidate] : []))}
              selectedPurchaseIds={documents.map((item) => item.eligibility.purchaseId)}
              supplierId={documents[0]?.eligibility.supplierId}
              documentType={documents[0]?.eligibility.documentType}
              onSelect={(candidate) => {
                void handlerAddDocument(candidate);
              }}
              disabled={busy}
            />
          )}

          {!!initialPurchaseId && !showAddDocument && documents.length > 0 && (
            <ActionButton
              label="Agregar otro documento"
              icon={<PlusCircle className="h-4 w-4" />}
              variant="secondary"
              size="sm"
              fullWidth={false}
              disabled={busy}
              onClick={() => setShowAddDocument(true)}
            />
          )}

          {initialPurchaseId && busy && documents.length === 0 && (
            <div className="rounded-xl border border-stroke bg-gray-1 px-5 py-6 text-center dark:border-strokedark dark:bg-meta-4">
              <p className="text-sm font-medium text-black dark:text-white">Consultando documento fiscal...</p>
              <p className="mt-1 text-xs text-body dark:text-bodydark">
                Validando si la compra puede participar en un Evento de Retorno.
              </p>
            </div>
          )}

          {documents.map(({ candidate, eligibility }) => (
            <section
              key={eligibility.purchaseId}
              className="overflow-hidden rounded-xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark"
            >
              <header className="bg-gray-1 flex flex-col gap-3 border-b border-stroke p-4 dark:border-strokedark dark:bg-meta-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <FileCheck2 className="h-5 w-5 shrink-0 text-primary" />
                    <h3 className="truncate font-semibold text-black dark:text-white">
                      {candidate?.documentNumber || 'Documento fiscal seleccionado'}
                    </h3>
                  </div>
                  <p
                    className="mt-1 max-w-3xl truncate text-xs text-body dark:text-bodydark"
                    title={candidate?.controlNumber || eligibility.generationCode}
                  >
                    {candidate?.controlNumber || eligibility.generationCode || 'Código fiscal disponible al consultar el detalle'}
                  </p>
                </div>

                <ActionButton
                  label="Quitar"
                  icon={<Trash2 className="h-4 w-4" />}
                  variant="danger"
                  size="sm"
                  fullWidth={false}
                  disabled={busy}
                  onClick={() => handlerRemoveDocument(eligibility.purchaseId)}
                />
              </header>

              <div className="p-4">
                <dl className="mb-5 grid gap-4 rounded-xl bg-primary/5 p-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
                  <div className="min-w-0">
                    <dt className="text-xs font-medium text-body dark:text-bodydark">Tipo DTE</dt>
                    <dd className="mt-1 font-semibold text-black dark:text-white">{typeLabel(eligibility.documentType)}</dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-xs font-medium text-body dark:text-bodydark">Sello de recepción</dt>
                    <dd className="mt-1 truncate font-medium text-black dark:text-white" title={eligibility.receptionSeal}>
                      {eligibility.receptionSeal || '—'}
                    </dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-xs font-medium text-body dark:text-bodydark">Fecha límite</dt>
                    <dd className="mt-1 font-medium text-black dark:text-white">{dateLabel(eligibility.deadline)}</dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-xs font-medium text-body dark:text-bodydark">Código de generación</dt>
                    <dd className="mt-1 truncate font-medium text-black dark:text-white" title={eligibility.generationCode}>
                      {eligibility.generationCode || '—'}
                    </dd>
                  </div>
                </dl>

                <div className="mb-4 flex flex-wrap gap-2">
                  <ActionButton
                    label="Retornar todo"
                    icon={<RotateCcw className="h-4 w-4" />}
                    size="sm"
                    fullWidth={false}
                    disabled={busy}
                    onClick={() => handlerSetAll(eligibility, true)}
                  />
                  <ActionButton
                    label="Limpiar selección"
                    variant="secondary"
                    size="sm"
                    fullWidth={false}
                    disabled={busy}
                    onClick={() => handlerSetAll(eligibility, false)}
                  />
                </div>

                {/* MOBILE */}
                <div className="grid gap-3 lg:hidden">
                  {eligibility.lines.map((line) => {
                    const key = purchaseReturnLineKey(eligibility.purchaseId, line.productId);
                    const selected = Boolean(selectedLines[key]);
                    const error = getLineError(eligibility.purchaseId, line.productId, line.availableQuantity);

                    return (
                      <article key={key} className="rounded-xl border border-stroke p-4 dark:border-strokedark">
                        <Checkbox
                          id={`select-mobile-${key}`}
                          label={`${line.productCode} — ${line.description}`}
                          checked={selected}
                          onChange={() =>
                            handlerChangeSelection(
                              eligibility.purchaseId,
                              line.productId,
                              !selected,
                              line.availableQuantity,
                              line.unitCost,
                              line.discount
                            )
                          }
                        />

                        <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg bg-gray-1 p-3 text-xs dark:bg-meta-4">
                          <div><span className="block text-body">Comprada</span><strong className="text-black dark:text-white">{line.soldQuantity}</strong></div>
                          <div><span className="block text-body">Retornada</span><strong className="text-black dark:text-white">{line.processedReturnQuantity}</strong></div>
                          <div><span className="block text-body">Disponible</span><strong className="text-black dark:text-white">{line.availableQuantity}</strong></div>
                          <div><span className="block text-body">Monto</span><strong className="text-black dark:text-white">{money(line.availableTotalAmount)}</strong></div>
                        </div>

                        <div className="mt-4 grid gap-3 sm:grid-cols-3">
                          <InputField
                            label="Cantidad a retornar"
                            ariaLabel={`Cantidad ${line.description}`}
                            name={`quantity-mobile-${key}`}
                            type="number"
                            min={0}
                            max={line.availableQuantity}
                            step="0.00000001"
                            value={quantities[key] || ''}
                            disabled={!selected || busy}
                            error={error}
                            onChange={(event) => handlerChangeQuantity(eligibility.purchaseId, line.productId, event.target.value)}
                          />
                          <InputField
                            label="Costo unitario"
                            ariaLabel={`Costo unitario ${line.description}`}
                            name={`cost-mobile-${key}`}
                            type="number"
                            min={0}
                            step="0.00000001"
                            value={unitCosts[key] ?? ''}
                            disabled={!selected || busy}
                            onChange={(event) => handlerChangeUnitCost(eligibility.purchaseId, line.productId, event.target.value)}
                          />
                          <InputField
                            label="Descuento"
                            ariaLabel={`Descuento ${line.description}`}
                            name={`discount-mobile-${key}`}
                            type="number"
                            min={0}
                            step="0.00000001"
                            value={discounts[key] ?? ''}
                            disabled={!selected || busy}
                            onChange={(event) => handlerChangeDiscount(eligibility.purchaseId, line.productId, event.target.value)}
                          />
                        </div>
                      </article>
                    );
                  })}
                </div>

                {/* DESKTOP */}
                <div className="hidden overflow-x-auto lg:block">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stroke text-left dark:border-strokedark">
                        <th className="p-3">Producto</th>
                        <th className="p-3">Comprada</th>
                        <th className="p-3">Retornada</th>
                        <th className="p-3">Disponible</th>
                        <th className="min-w-[150px] p-3">Costo unitario</th>
                        <th className="min-w-[150px] p-3">Descuento</th>
                        <th className="p-3">Monto disponible</th>
                        <th className="min-w-[190px] p-3">Cantidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eligibility.lines.map((line) => {
                        const key = purchaseReturnLineKey(eligibility.purchaseId, line.productId);
                        const selected = Boolean(selectedLines[key]);
                        const error = getLineError(eligibility.purchaseId, line.productId, line.availableQuantity);

                        return (
                          <tr key={key} className="border-b border-stroke align-top dark:border-strokedark">
                            <td className="p-3">
                              <Checkbox
                                id={`select-desktop-${key}`}
                                label={`${line.productCode} — ${line.description}`}
                                checked={selected}
                                onChange={() =>
                                  handlerChangeSelection(
                                    eligibility.purchaseId,
                                    line.productId,
                                    !selected,
                                    line.availableQuantity,
                                    line.unitCost,
                                    line.discount
                                  )
                                }
                              />
                            </td>
                            <td className="p-3">{line.soldQuantity}</td>
                            <td className="p-3">{line.processedReturnQuantity}</td>
                            <td className="p-3">{line.availableQuantity}</td>
                            <td className="p-3">
                              <InputField
                                label="Costo unitario"
                                ariaLabel={`Costo unitario ${line.description}`}
                                name={`cost-${key}`}
                                type="number"
                                min={0}
                                step="0.00000001"
                                value={unitCosts[key] ?? ''}
                                disabled={!selected || busy}
                                onChange={(event) => handlerChangeUnitCost(eligibility.purchaseId, line.productId, event.target.value)}
                              />
                            </td>
                            <td className="p-3">
                              <InputField
                                label="Descuento"
                                ariaLabel={`Descuento ${line.description}`}
                                name={`discount-${key}`}
                                type="number"
                                min={0}
                                step="0.00000001"
                                value={discounts[key] ?? ''}
                                disabled={!selected || busy}
                                onChange={(event) => handlerChangeDiscount(eligibility.purchaseId, line.productId, event.target.value)}
                              />
                            </td>
                            <td className="p-3">{money(line.availableTotalAmount)}</td>
                            <td className="p-3">
                              <InputField
                                label="Cantidad a retornar"
                                ariaLabel={`Cantidad ${line.description}`}
                                name={`quantity-${key}`}
                                type="number"
                                min={0}
                                max={line.availableQuantity}
                                step="0.00000001"
                                value={quantities[key] || ''}
                                disabled={!selected || busy}
                                error={error}
                                onChange={(event) => handlerChangeQuantity(eligibility.purchaseId, line.productId, event.target.value)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          ))}

          {documents.length > 0 && (
            <section className="rounded-xl border border-primary/30 bg-primary/5 p-5">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="font-semibold text-black dark:text-white">Resumen fiscal</h3>
                  <p className="mt-1 text-xs text-body dark:text-bodydark">
                    {previewLoading
                      ? 'Calculando totales...'
                      : 'Se actualiza automáticamente al editar cantidades, costos o descuentos.'}
                  </p>
                </div>

                {preview && (
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      preview.valid ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                    }`}
                  >
                    {preview.valid ? 'Selección válida' : 'Requiere revisión'}
                  </span>
                )}
              </div>

              {preview && (
                <>
                  <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                    <PreviewMetric label="Documentos" value={String(preview.documentCount)} />
                    <PreviewMetric label="Líneas" value={String(preview.lineCount)} />
                    <PreviewMetric label="Cantidad" value={String(preview.totalQuantity)} />
                    <PreviewMetric label="Monto neto" value={money(preview.totalNetAmount)} />
                    <PreviewMetric label="Total del evento" value={money(preview.totalEventAmount)} />
                    <PreviewMetric label="Total del documento" value={money(preview.totalDocumentAmount)} />
                  </div>

                  {!!preview.warnings?.length && (
                    <div className="mt-4 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3">
                      <p className="mb-2 text-sm font-semibold text-warning">Advertencias</p>
                      <ul className="list-disc space-y-1 pl-5 text-sm text-black dark:text-white">
                        {preview.warnings.map((warning) => (
                          <li key={warning}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {!!validationErrors.length && documents.length > 0 && (
            <div className="rounded-xl border border-danger/20 bg-danger/5 px-4 py-3">
              <p className="text-sm font-medium text-danger">
                Existen productos con cantidades que deben ser revisadas antes de continuar.
              </p>
            </div>
          )}

          <div className="flex flex-col-reverse justify-end gap-2 border-t border-stroke pt-4 dark:border-strokedark sm:flex-row">
            <ActionButton label="Cancelar" variant="secondary" fullWidth={false} disabled={busy} onClick={handleClose} />
            <ActionButton
              label="Confirmar y generar"
              loadingLabel="Generando..."
              loading={busy}
              disabled={!canSubmit}
              fullWidth={false}
              onClick={() => {
                void handlerSubmit();
              }}
            />
          </div>
        </>
      )}

      {result && (
        <section
          className={`space-y-5 rounded-xl border p-5 ${
            result.status === 'REJECTED'
              ? 'border-danger/30 bg-danger/5'
              : 'border-success/30 bg-success/5'
          }`}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-body dark:text-bodydark">Resultado</p>
              <FiscalEventStatusBadge status={result.status} />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-black dark:text-white">
              {resultTitleByStatus[result.status] || 'Evento de Retorno'}
            </h3>
            <p className="mt-2 text-sm leading-6 text-body dark:text-bodydark">
              {resultMessageByStatus[result.status]}
            </p>
          </div>

          {(result.status === 'PROCESSED' || result.status === 'REJECTED') && eventDetail && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                result.status === 'REJECTED'
                  ? 'border-danger/20 bg-white dark:bg-boxdark'
                  : 'border-success/20 bg-white dark:bg-boxdark'
              }`}
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                {eventDetail.classificationCode && (
                  <div>
                    <dt className="text-xs text-body dark:text-bodydark">Código de clasificación</dt>
                    <dd className="font-medium text-black dark:text-white">{eventDetail.classificationCode}</dd>
                  </div>
                )}
                {eventDetail.messageCode && (
                  <div>
                    <dt className="text-xs text-body dark:text-bodydark">Código de mensaje</dt>
                    <dd className="font-medium text-black dark:text-white">{eventDetail.messageCode}</dd>
                  </div>
                )}
                {eventDetail.messageDescription && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs text-body dark:text-bodydark">Descripción</dt>
                    <dd className="font-medium text-black dark:text-white">{eventDetail.messageDescription}</dd>
                  </div>
                )}
                {eventDetail.receptionSeal && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs text-body dark:text-bodydark">Sello de recepción</dt>
                    <dd className="break-all font-medium text-black dark:text-white">{eventDetail.receptionSeal}</dd>
                  </div>
                )}
              </dl>

              {!!eventDetail.observations?.length && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-body dark:text-bodydark">Observaciones</p>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-black dark:text-white">
                    {eventDetail.observations.map((observation) => (
                      <li key={observation}>{observation}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {result.pdfAvailable && (
            <div className="border-t border-stroke pt-4 dark:border-strokedark">
              <FiscalEventDownloads eventId={result.id} eventType={result.eventType} status={result.status} />
            </div>
          )}

          <div className="flex flex-wrap gap-2 border-t border-stroke pt-4 dark:border-strokedark">
            {result.status === 'DRAFT' && (
              <ActionButton
                label="Validar"
                loadingLabel="Validando..."
                loading={busy}
                fullWidth={false}
                onClick={() => {
                  void handlerValidate();
                }}
              />
            )}

            {result.status === 'VALIDATED' && (
              <ActionButton
                label="Firmar"
                loadingLabel="Firmando..."
                loading={busy}
                fullWidth={false}
                onClick={() => {
                  void handlerSign();
                }}
              />
            )}

            {result.status === 'SIGNED' && (
              <ActionButton
                label="Enviar a Hacienda"
                loadingLabel="Transmitiendo..."
                loading={busy}
                fullWidth={false}
                onClick={() => {
                  void handlerSend();
                }}
              />
            )}
          </div>

          <div className="flex justify-end border-t border-stroke pt-4 dark:border-strokedark">
            <ActionButton label="Cerrar" fullWidth={false} onClick={handleClose} />
          </div>
        </section>
      )}
    </div>
  );

  if (embedded) {
    return body;
  }

  return (
    <ReusableModalWrapper isOpen={isOpen} onClose={handleClose} title="Crear evento de retorno de compra" maxWidth="max-w-6xl" hideFooter>
      {body}
    </ReusableModalWrapper>
  );
};

interface PreviewMetricProps {
  label: string;
  value: string;
}

const PreviewMetric = ({ label, value }: PreviewMetricProps) => (
  <div className="rounded-lg bg-white px-4 py-3 dark:bg-boxdark">
    <p className="text-xs text-body dark:text-bodydark">{label}</p>
    <p className="mt-1 font-semibold text-black dark:text-white">{value}</p>
  </div>
);

export default PurchaseReturnEventWizard;
